import CustomSheet from '@/components/shared/Sheet/Sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Command, CommandInput } from '@/components/ui/command';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import DateRangePicker from '@/pages/Dashboard/Partials/DateRangePicker';
import useModalStore from '@/store/useModalStore';
import {
  AffiliateReportFilterOption,
  AffiliateReportSortBy,
} from '@/@types/AffiliateReport';
import { ListFilter, X } from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import FilterAffiliateReport, {
  FILTER_AFFILIATE_REPORT_MODAL,
} from './FilterAffiliateReport.tsx';

const MAX_VISIBLE_FILTERS = 5;

interface Props {
  search: string;
  onSearchChange: (value: string) => void;
  dateRange: DateRange | undefined;
  onDateRangeChange: (range: DateRange | undefined) => void;
  sortBy: AffiliateReportSortBy;
  products: AffiliateReportFilterOption[];
  affiliates: AffiliateReportFilterOption[];
  offers: AffiliateReportFilterOption[];
  checkouts: AffiliateReportFilterOption[];
  templates: AffiliateReportFilterOption[];
  onApplyFilters: (data: {
    products?: AffiliateReportFilterOption[];
    affiliates?: AffiliateReportFilterOption[];
    offers?: AffiliateReportFilterOption[];
    checkouts?: AffiliateReportFilterOption[];
    templates?: AffiliateReportFilterOption[];
    sortBy?: AffiliateReportSortBy;
  }) => void;
  onRemoveProduct: (value: string) => void;
  onRemoveAffiliate: (value: string) => void;
  onRemoveOffer: (value: string) => void;
  onRemoveCheckout: (value: string) => void;
  onRemoveTemplate: (value: string) => void;
  onRemoveSort: () => void;
  clearAllFilters: () => void;
  hasActiveFiltersBeyondDates: boolean;
}

export default function AffiliateReportToolbar({
  search,
  onSearchChange,
  dateRange,
  onDateRangeChange,
  sortBy,
  products,
  affiliates,
  offers,
  checkouts,
  templates,
  onApplyFilters,
  onRemoveProduct,
  onRemoveAffiliate,
  onRemoveOffer,
  onRemoveCheckout,
  onRemoveTemplate,
  onRemoveSort,
  clearAllFilters,
  hasActiveFiltersBeyondDates,
}: Props) {
  const { t } = useTranslation(['reports', 'sales', 'common']);
  const { openModal, modals, closeModal } = useModalStore();

  const sortLabelMap: Record<AffiliateReportSortBy, string> = {
    sales: t('reports:affiliateReport.sort.sales'),
    revenue: t('reports:affiliateReport.sort.revenue'),
    profit: t('reports:affiliateReport.sort.profit'),
  };

  const allFilterBadges = [
    ...(sortBy !== 'revenue'
      ? [
          {
            key: 'sort',
            group: t('reports:affiliateReport.sort.label'),
            label: sortLabelMap[sortBy],
            onClick: onRemoveSort,
          },
        ]
      : []),
    ...products.map((p) => ({
      key: `product-${p.value}`,
      group: t('sales:products'),
      label: p.label,
      onClick: () => onRemoveProduct(p.value),
    })),
    ...affiliates.map((a) => ({
      key: `affiliate-${a.value}`,
      group: t('sales:affiliate'),
      label: a.label,
      onClick: () => onRemoveAffiliate(a.value),
    })),
    ...offers.map((o) => ({
      key: `offer-${o.value}`,
      group: t('sales:offer'),
      label: o.label,
      onClick: () => onRemoveOffer(o.value),
    })),
    ...checkouts.map((c) => ({
      key: `checkout-${c.value}`,
      group: t('reports:checkout'),
      label: c.label,
      onClick: () => onRemoveCheckout(c.value),
    })),
    ...templates.map((c) => ({
      key: `template-${c.value}`,
      group: t('reports:template'),
      label: c.label,
      onClick: () => onRemoveTemplate(c.value),
    })),
  ];

  return (
    <>
      <CustomSheet
        open={modals[FILTER_AFFILIATE_REPORT_MODAL]}
        onClose={() => closeModal(FILTER_AFFILIATE_REPORT_MODAL)}
      >
        <FilterAffiliateReport
          filterValues={{
            products,
            affiliates,
            offers,
            checkouts,
            templates,
            sortBy,
          }}
          dateRange={dateRange}
          onDateRangeChange={onDateRangeChange}
          onFilter={(data) => {
            onApplyFilters({
              products: (data.products ?? []) as AffiliateReportFilterOption[],
              affiliates: (data.affiliates ?? []) as AffiliateReportFilterOption[],
              offers: (data.offers ?? []) as AffiliateReportFilterOption[],
              checkouts: (data.checkouts ?? []) as AffiliateReportFilterOption[],
              templates: (data.templates ?? []) as AffiliateReportFilterOption[],
              sortBy: data.sortBy as AffiliateReportSortBy | undefined,
            });
          }}
        />
      </CustomSheet>

      <Card className="p-3 gap-3 flex flex-col">
        {/* Row: date range + search + filter button */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="flex items-center justify-between gap-3 sm:shrink-0">
            <DateRangePicker
              defaultValue={dateRange}
              onChange={onDateRangeChange}
            />
            <Button
              className="flex sm:hidden shrink-0"
              variant="secondary"
              onClick={() => openModal(FILTER_AFFILIATE_REPORT_MODAL)}
              aria-label={t('sales:filter')}
            >
              <ListFilter size={18} />
            </Button>
          </div>

          <Command className="flex-1 min-w-0">
            <CommandInput
              defaultValue={search}
              placeholder={t('reports:affiliateReport.search_placeholder')}
              onValueChange={onSearchChange}
              data-cy="search-affiliate-report"
            />
          </Command>

          <Button
            className="hidden sm:flex shrink-0"
            variant="secondary"
            onClick={() => openModal(FILTER_AFFILIATE_REPORT_MODAL)}
            data-cy="open-filter-affiliate-report"
          >
            <ListFilter size={18} />
            {t('sales:filter')}
          </Button>
        </div>

        {/* Active filter badges */}
        {hasActiveFiltersBeyondDates && (
          <div className="flex flex-wrap items-center gap-2">
            {allFilterBadges.slice(0, MAX_VISIBLE_FILTERS).map((badge) => (
              <Badge
                key={badge.key}
                className="flex font-medium min-h-7 items-center gap-2 bg-[#18181b] text-colorText hover:bg-[#1f1f22] cursor-default"
              >
                <div className="flex items-center gap-1 min-w-0">
                  <span className="text-muted shrink-0 text-xs">{badge.group}</span>
                  <span className="truncate max-w-[120px] sm:max-w-[180px] text-xs">
                    {badge.label}
                  </span>
                </div>
                <button
                  onClick={badge.onClick}
                  className="text-muted hover:text-white cursor-pointer shrink-0 ml-1"
                  type="button"
                  aria-label={`${t('common:remove')} ${badge.label}`}
                >
                  <X size={12} />
                </button>
              </Badge>
            ))}

            {allFilterBadges.length > MAX_VISIBLE_FILTERS && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="cursor-pointer rounded bg-[#27272A] px-2 py-1 text-xs font-semibold flex items-center justify-center text-zinc-300">
                    +{allFilterBadges.length - MAX_VISIBLE_FILTERS}
                  </div>
                </TooltipTrigger>
                <TooltipContent className="w-72 p-4 bg-[#18181b] text-colorText border border-[#333]">
                  <h4 className="font-semibold mb-2 text-sm">{t('sales:filters')}</h4>
                  <div className="flex flex-col gap-2 max-h-52 overflow-y-auto">
                    {allFilterBadges.slice(MAX_VISIBLE_FILTERS).map((badge) => (
                      <div
                        key={badge.key}
                        className="flex items-center justify-between text-sm gap-3"
                      >
                        <div className="flex items-center gap-1 min-w-0">
                          <span className="text-muted shrink-0">{badge.group}</span>
                          <span className="truncate text-zinc-200">{badge.label}</span>
                        </div>
                        <button
                          onClick={badge.onClick}
                          className="text-muted hover:text-white shrink-0"
                          type="button"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </TooltipContent>
              </Tooltip>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              data-cy="clear-affiliate-report-filters"
              className="h-7 text-interactive hover:text-interactive/80 px-2"
            >
              {t('common:clear_all_filters')}
            </Button>
          </div>
        )}
      </Card>
    </>
  );
}
